<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, watch, toRaw, inject } from "vue"
import { useForm } from "vee-validate"
import { useI18n } from "vue-i18n"
import { useDebounceFn } from "@vueuse/core"
import * as yup from "yup"

import { useFetchWrapper } from "@core/composables"
import { ServiceError } from "@core/exceptions"
import { useAuthStore } from "@core/stores/auth.store"
import { formatPhone } from "@core/helpers"
import { Services } from "@services"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            user: {
                form: {
                    reset_password_link: "Changer votre mot de passe",
                    reset_password_success:
                        "Un email de réinitialisation de mot de passe vous a été envoyé.",
                    reset_password_error:
                        "Impossible d'envoyer l'email de réinitialisation.",
                    save: "Sauvegarder",
                    save_success: "Vos informations ont bien été mises à jour.",
                    save_error:
                        "Une erreur est survenue lors de la mise à jour de vos informations.",
                    validation_error: "Le formulaire contient des erreurs.",
                    load_error:
                        "Erreur lors du chargement des informations utilisateur.",
                    firstname_required: "Le prénom est obligatoire.",
                    lastname_required: "Le nom est obligatoire.",
                    phone_invalid:
                        "Le format du numéro de téléphone est invalide.",
                    lastname: "Nom",
                    firstname: "Prénom",
                    email: "Adresse email",
                    phone: "Numéro de téléphone",
                    phone_placeholder: "Ex : 06 12 34 56 78",
                    password: "Mot de passe",
                },
            },
        },
    },
})

// ================================
// COMPOSABLES & STORES
// ================================

/**
 * Store d'authentification pour accéder aux données utilisateur
 */
const authStore = useAuthStore()

const { $updateCurrentUser, $sendResetPasswordEmail } =
    inject<Services>("services")!

// ================================
// ÉTAT DES DONNÉES
// ================================

/**
 * Snapshot du formulaire pour détecter les changements
 */
const snapshotForm = ref<string | null>(null)

/**
 * Timeout pour masquer le message de réinitialisation de mot de passe
 */
const timeoutResetPassword = ref<NodeJS.Timeout | null>(null)

/**
 * Timeout pour masquer le message de succès après sauvegarde
 */
const timeoutSuccess = ref<NodeJS.Timeout | null>(null)

/**
 * Affichage du mot de passe masqué
 */
const passwordDisplay = "• • • • • • • • • • • • • • • • •"

// ================================
// DONNÉES DE CONFIGURATION
// ================================

/**
 * Expression régulière pour valider le format du numéro de téléphone français
 */
const phoneRegex = /^((\+)33|0)[1-9](\d{2}){4}$/

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Détermine si le formulaire a été modifié
 * @returns true si des modifications ont été apportées, false sinon
 */
const getHasDataChanged = computed((): boolean => {
    if (!snapshotForm.value) return false
    const currentForm = JSON.stringify({
        firstName: modelFirstName.value,
        lastName: modelLastName.value,
        phone: modelPhone.value,
    })
    return snapshotForm.value !== currentForm
})

/**
 * Détermine si le bouton de sauvegarde doit être désactivé
 * @returns true si le formulaire est en cours de chargement ou de soumission
 */
const getIsSaveDisabled = computed((): boolean => {
    return isSubmitting.value || !getHasDataChanged.value
})

/**
 * Indique si les données utilisateur sont chargées
 */
const getIsUserDataLoaded = computed((): boolean => {
    return !!authStore.userId
})

// ================================
// FORMULAIRE & VALIDATION
// ================================

/**
 * Schéma de validation du formulaire
 */
const schema = yup.object({
    firstName: yup
        .string()
        .required(t("user.form.firstname_required"))
        .trim()
        .test("not-empty", t("user.form.firstname_required"), (value) => {
            return value !== undefined && value.trim() !== ""
        }),
    lastName: yup
        .string()
        .required(t("user.form.lastname_required"))
        .trim()
        .test("not-empty", t("user.form.lastname_required"), (value) => {
            return value !== undefined && value.trim() !== ""
        }),
    phone: yup
        .string()
        .nullable()
        .test("phone-format", t("user.form.phone_invalid"), (value) => {
            if (!value || value.trim() === "") return true
            const phoneWithoutSpaces = value.replace(/ /g, "")
            return phoneRegex.test(phoneWithoutSpaces)
        }),
})

const { errors, defineField, handleSubmit, meta } = useForm({
    validationSchema: schema,
})

const [modelFirstName] = defineField("firstName")
const [modelLastName] = defineField("lastName")
const [modelPhone] = defineField("phone")

// ================================
// INITIALISATION DES DONNÉES
// ================================

/**
 * Initialise le formulaire avec les données du store
 */
function initializeForm() {
    if (!getIsUserDataLoaded.value) return

    modelFirstName.value = authStore.firstname || ""
    modelLastName.value = authStore.lastname || ""
    modelPhone.value = formatPhone(authStore.phone || "")

    // Crée un snapshot du formulaire
    snapshotForm.value = JSON.stringify(
        toRaw({
            firstName: modelFirstName.value,
            lastName: modelLastName.value,
            phone: modelPhone.value,
        })
    )
}

// ================================
// SAUVEGARDE
// ================================

/**
 * Sauvegarde les informations utilisateur modifiées
 */
const {
    fetch: onUpdateUserInfo,
    isFetching: isSubmitting,
    hasError: hasErrorSubmitting,
    error: errorSubmitting,
    isSuccess: isSuccessSubmitting,
} = useFetchWrapper(async () => {
    if (timeoutSuccess.value) {
        clearTimeout(timeoutSuccess.value)
    }

    const updatedUser = await $updateCurrentUser({
        firstname: modelFirstName.value,
        lastname: modelLastName.value,
        phone: modelPhone.value ? modelPhone.value.replace(/ /g, "") : null,
    })

    // Met à jour le formulaire avec les données retournées par l'API
    modelFirstName.value = updatedUser.firstname ?? modelFirstName.value
    modelLastName.value = updatedUser.lastname ?? modelLastName.value
    modelPhone.value = formatPhone(updatedUser.phone ?? modelPhone.value)

    // Met à jour le store
    authStore.updateUserInfo({
        firstname: updatedUser.firstname,
        lastname: updatedUser.lastname,
        phone: updatedUser.phone,
    })

    // Met à jour le snapshot après un court délai pour permettre l'affichage du message de succès
    setTimeout(() => {
        snapshotForm.value = JSON.stringify(
            toRaw({
                firstName: modelFirstName.value,
                lastName: modelLastName.value,
                phone: modelPhone.value,
            })
        )
    }, 1000)

    timeoutSuccess.value = setTimeout(() => {
        isSuccessSubmitting.value = false
    }, 3000)
})

/**
 * Gère les erreurs de validation lors de la soumission
 * @param validationErrors - Erreurs de validation
 */
const onInvalidSubmit = () => {
    errorSubmitting.value = new ServiceError({
        message: t("user.form.validation_error"),
        code: "VALIDATION_ERROR",
    })
}

const onSubmit = handleSubmit(onUpdateUserInfo, onInvalidSubmit)

watch(
    meta,
    (newMeta) => {
        if (newMeta?.valid) {
            errorSubmitting.value = null
        }
    },
    {
        deep: true,
        immediate: true,
    }
)

// ================================
// RÉINITIALISATION MOT DE PASSE
// ================================

/**
 * Envoie un email de réinitialisation de mot de passe
 */
const {
    fetch: onSendResetPasswordEmail,
    isSuccess: isSuccessSendingResetPassword,
    hasError: hasErrorSendingResetPassword,
} = useFetchWrapper(async () => {
    if (timeoutResetPassword.value) {
        clearTimeout(timeoutResetPassword.value)
    }

    await $sendResetPasswordEmail(authStore.username)

    // Masque le message après 4 secondes
    timeoutResetPassword.value = setTimeout(() => {
        isSuccessSendingResetPassword.value = false
    }, 4000)
})

/**
 * Fonction debounced pour éviter les clics multiples sur le bouton de réinitialisation
 */
const onSendResetPasswordEmailDebounced = useDebounceFn(() => {
    onSendResetPasswordEmail()
}, 500)

// ================================
// CYCLE DE VIE
// ================================

/**
 * Surveille les changements de phone et formate automatiquement
 */
watch(
    () => modelPhone.value,
    (newValue) => {
        if (!newValue) return
        const formatted = formatPhone(newValue)
        if (formatted !== newValue) modelPhone.value = formatted
    }
)

/**
 * Surveille les changements de userId et initialise le formulaire
 */
watch(
    () => authStore.userId,
    (userId) => {
        if (userId && getIsUserDataLoaded.value) {
            initializeForm()
        }
    },
    { immediate: true }
)

/**
 * Surveille les changements des données utilisateur dans le store
 */
watch(
    () => [authStore.firstname, authStore.lastname, authStore.phone],
    () => {
        if (getIsUserDataLoaded.value && !snapshotForm.value) {
            initializeForm()
        }
    },
    { immediate: true }
)
</script>

<template>
    <BaseCard v-if="getIsUserDataLoaded">
        <template #content>
            <div class="form-user-info flex flex-col gap-4">
                <!-- ================================ -->
                <!-- FORMULAIRE PRINCIPAL -->
                <!-- ================================ -->
                <form
                    class="form-user-info__form grid grid-cols-1 md:grid-cols-2 gap-4"
                    @submit.prevent="onSubmit"
                >
                    <!-- ================================ -->
                    <!-- NOM -->
                    <!-- ================================ -->
                    <BaseInputWrapper
                        input-id="user-lastname"
                        :label="t('user.form.lastname')"
                        :error="errors.lastName"
                    >
                        <BaseInputText
                            v-model="modelLastName"
                            input-id="user-lastname"
                            data-cy="user-lastname"
                            :invalid="!!errors.lastName"
                        />
                    </BaseInputWrapper>

                    <!-- ================================ -->
                    <!-- PRÉNOM -->
                    <!-- ================================ -->
                    <BaseInputWrapper
                        input-id="user-firstname"
                        :label="t('user.form.firstname')"
                        :error="errors.firstName"
                    >
                        <BaseInputText
                            v-model="modelFirstName"
                            input-id="user-firstname"
                            data-cy="user-firstname"
                            :invalid="!!errors.firstName"
                        />
                    </BaseInputWrapper>

                    <!-- ================================ -->
                    <!-- EMAIL (LECTURE SEULE) -->
                    <!-- ================================ -->
                    <BaseInputWrapper
                        input-id="user-username"
                        :label="t('user.form.email')"
                    >
                        <BaseInputText
                            :model-value="authStore.username"
                            input-id="user-username"
                            data-cy="user-username"
                            disabled
                        />
                    </BaseInputWrapper>

                    <!-- ================================ -->
                    <!-- TÉLÉPHONE -->
                    <!-- ================================ -->
                    <BaseInputWrapper
                        input-id="user-phone"
                        :label="t('user.form.phone')"
                        :error="errors.phone"
                    >
                        <BaseInputText
                            v-model="modelPhone"
                            input-id="user-phone"
                            data-cy="user-phone"
                            :placeholder="t('user.form.phone_placeholder')"
                            :invalid="!!errors.phone"
                        />
                    </BaseInputWrapper>

                    <!-- ================================ -->
                    <!-- MOT DE PASSE AVEC LIEN DE RÉINITIALISATION -->
                    <!-- ================================ -->
                    <div class="form-user-info__password">
                        <BaseInputWrapper
                            input-id="user-password"
                            :label="t('user.form.password')"
                        >
                            <BaseInputText
                                :model-value="passwordDisplay"
                                input-id="user-password"
                                data-cy="user-password"
                                disabled
                            />
                        </BaseInputWrapper>
                        <a
                            class="form-user-info__reset-password-link text-blue-600 hover:text-blue-800 cursor-pointer text-sm mt-1"
                            @click.prevent.stop="
                                onSendResetPasswordEmailDebounced
                            "
                        >
                            {{ t("user.form.reset_password_link") }}
                        </a>
                    </div>

                    <!-- ================================ -->
                    <!-- ACTIONS -->
                    <!-- ================================ -->
                    <div class="flex flex-col gap-4 pt-4 md:col-span-2">
                        <BaseButton
                            class="w-fit mx-auto"
                            type="submit"
                            :label="t('user.form.save')"
                            :loading="isSubmitting"
                            :disabled="getIsSaveDisabled"
                        />
                        <!-- ================================ -->
                        <!-- MESSAGE D'ERREUR DE SOUMISSION -->
                        <!-- ================================ -->
                        <BaseMessage
                            v-if="hasErrorSubmitting"
                            severity="error"
                            class="mb-4 md:col-span-2"
                        >
                            {{
                                errorSubmitting?.message ||
                                t("user.form.save_error")
                            }}
                        </BaseMessage>
                        <!-- ================================ -->
                        <!-- MESSAGE DE SUCCÈS DE SAUVEGARDE DES INFORMATIONS UTILISATEUR -->
                        <!-- ================================ -->
                        <BaseMessage
                            v-if="isSuccessSubmitting"
                            severity="success"
                            class="mb-4"
                        >
                            {{ t("user.form.save_success") }}
                        </BaseMessage>
                        <!-- ================================ -->
                        <!-- MESSAGE DE SUCCÈS DE RÉINITIALISATION DE MOT DE PASSE -->
                        <!-- ================================ -->
                        <BaseMessage
                            v-if="isSuccessSendingResetPassword"
                            severity="success"
                            :closable="false"
                            class="mt-2"
                        >
                            {{ t("user.form.reset_password_success") }}
                        </BaseMessage>
                        <!-- ================================ -->
                        <!-- MESSAGE D'ERREUR DE RÉINITIALISATION DE MOT DE PASSE -->
                        <!-- ================================ -->
                        <BaseMessage
                            v-if="hasErrorSendingResetPassword"
                            severity="error"
                            :closable="false"
                            class="mt-2"
                        >
                            {{ t("user.form.reset_password_error") }}
                        </BaseMessage>
                    </div>
                </form>
            </div>
        </template>
    </BaseCard>
    <BaseSkeleton v-else width="100%" height="40rem" />
</template>
